import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import { AppContext } from "components/context/AppContext.js";
import styled from "styled-components";
import tw from "twin.macro";
import { ReactComponent as SvgDotPatternIcon } from "../../images/dot-pattern.svg";
import ReactModalAdapter from "../../helpers/ReactModalAdapter.js";
import { NotificationContainer, NotificationManager } from "react-notifications";
import Datetime from 'react-datetime';
import moment from 'moment';
import Dropdown from 'react-dropdown';
import 'react-dropdown/style.css';
import { useVisitors } from "hooks/useVisitors";

const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24`;

const FormContainer = styled.div`
  ${tw`p-10 sm:p-12 md:p-16 bg-primary-500 text-gray-100 rounded-lg relative`}
  form {
    ${tw`mt-4`}
  }
  h2 {
    ${tw`text-3xl sm:text-4xl font-bold`}
  }
  input,textarea,select {
    ${tw`w-full bg-transparent text-gray-100 text-base font-medium tracking-wide border-b-2 py-2 text-gray-100 hocus:border-pink-400 focus:outline-none transition duration-200`};

    ::placeholder {
      ${tw`text-gray-500`}
    }
  }
  option {
    ${tw`w-full font-medium tracking-wide border-b-2 py-2 transition duration-200`};
  }
`;

const TwoColumn = tw.div`flex flex-col sm:flex-row justify-between`;
const Column = tw.div`sm:w-5/12 flex flex-col`;
const InputContainer = tw.div`relative py-5 mt-6`;
const Label = tw.label`absolute top-0 left-0 tracking-wide font-semibold text-sm`;
const Input = tw.input``;
const TextArea = tw.textarea`h-24 sm:h-full resize-none`;
const TextAreaLink = tw.textarea`w-full sm:w-10/12 h-full sm:h-20 text-sm sm:text-sm resize-none`;
const SubmitButton = tw.button`w-full sm:w-32 mt-6 py-3 bg-gray-100 text-primary-500 rounded-full font-bold tracking-wide shadow-lg uppercase text-sm transition duration-300 transform focus:outline-none focus:shadow-outline hover:bg-gray-300 hover:text-primary-700 hocus:-translate-y-px hocus:shadow-xl`;
const Button = tw.button`w-full sm:w-48 mt-6 py-3 bg-green-500 text-indigo-100 rounded-full font-bold tracking-wide shadow-lg uppercase text-sm transition duration-300 transform focus:outline-none focus:shadow-outline hover:bg-green-300 hover:text-primary-700 hocus:-translate-y-px hocus:shadow-xl`;
const Calendar = tw(Datetime)`text-blue-400`;
const Dropdown2 = tw(Dropdown)`mt-6 first:mt-0 border-b-2 py-3 focus:outline-none font-medium transition duration-300 hocus:border-primary-500`;

const SvgDotPattern1 = tw(SvgDotPatternIcon)`absolute bottom-0 right-0 transform translate-y-1/2 translate-x-1/2 -z-10 opacity-50 text-primary-500 fill-current w-24`;

const StyledModal = styled(ReactModalAdapter)`
  &.mainHeroModal__overlay {
    ${tw`fixed inset-0 z-50`}
  }
  &.mainHeroModal__content {
    ${tw`xl:mx-auto m-4 sm:m-16 max-w-screen-xl absolute inset-0 flex justify-center items-center rounded-lg bg-gray-200 outline-none`}
  }
  .content {
    ${tw`w-full lg:p-16`}
  }
`;
const CloseModalButton = tw.button`absolute top-0 right-0 mt-8 mr-8 hocus:text-primary-500`;



const HighlightedText = tw.span`bg-primary-500 text-gray-100 px-4 transform -skew-x-12 inline-block`;
const imageCss = tw`rounded-4xl`;
const Subheading = tw.span`tracking-wider text-sm font-medium`;
const Description = tw.div`inline-block mt-8`;



const RegisterForm = ({ onVisitorCreated }) => {
  const { state } = useContext(AppContext);
  const user = state.user;
  let visita = 'Visita';
  const { createVisitor, fetchVisitors } = useVisitors();
  const [isEvent, setIsEvent] = useState(false);
  const [isGroup, setIsGroup] = useState(false);
  const [isEndDateRequired, setIsEndDateRequired] = useState(false);
  const [title, setTitle] = useState('Nombre del Visitante');
  const [telTitle, setTelTitle] = useState('Teléfono del Visitante');
  const [phoneError, setPhoneError] = useState('');
  const visitTypes = [
    'Visita', 'Inquilino', 'Proveedor', 'EventoGrupal'
  ];
  const defaultOption = visitTypes[0];

  // E.164 validation per architecture spec (AI_INTEGRATION_INSTRUCTIONS.md §5.0.1)
  const E164_REGEX = /^\+[1-9]\d{7,14}$/;

  /**
   * Normalises a Mexican 10-digit number to E.164 as a best-effort helper.
   * If the input already has a + prefix we validate as-is.
   */
  const normalizePhone = (raw) => {
    const cleaned = raw.replace(/[\s\-().]/g, '');
    if (cleaned.startsWith('+')) return cleaned;
    // Auto-prepend Mexico +52 for 10-digit local numbers
    if (/^[0-9]{10}$/.test(cleaned)) return `+52${cleaned}`;
    return cleaned;
  };

  const validatePhone = (value) => {
    const normalized = normalizePhone(value);
    if (!E164_REGEX.test(normalized)) {
      setPhoneError('Número inválido. Usa formato internacional: +525512345678');
      return null;
    }
    setPhoneError('');
    return normalized;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const { nombre, telefono, comentario, maxOpenDoorTimes, comentario2 } = event.target.elements;
    const ingreso = event.target.elements[2];
    const salida = event.target.elements[3];

    const normalizedPhone = validatePhone(telefono.value);
    if (!normalizedPhone) return;

    if (user) {
      const visitor = {
        Name: nombre.value,
        PhoneNumber: normalizedPhone,
        Reason: visita,
        StarDate: moment(new Date(ingreso.value)).format("YYYY/MM/DD HH:mm:ss"),
        EndDate: moment(new Date(salida.value)).format("YYYY/MM/DD HH:mm:ss"),
        HomeownerId: user.homeownerId,
        SubId: user.homeowner?.suburbId || null,
        UnlimitedAccess: false,
        MaxOpenDoorTimes: !isEvent ? 1 : maxOpenDoorTimes?.value,
        Comentario: !isEvent ? comentario.value : comentario2.value,
        IsGroup: isGroup
      }

      await createVisitor(visitor).then(async (response) => {
        if (response.success) {
          if (typeof onVisitorCreated === 'function') {
            onVisitorCreated(response.data.visitor?.guid);
          }
          createNotification('success', 'Visita creada con éxito', 'Éxito');
          //refresh visitors
          await fetchVisitors(user.homeownerId).then((response) => {
            if (response.data.visitors.length > 0) {
              // Do something with the visitors data
            }
          }).catch((error) => {
            console.log("Failed to get visitors: " + error);
            NotificationManager.error(error.message, "Error al recargar visitantes", 5000);
          });

        }
        else {
          createNotification('info', response.data.statusCode, response.data.errorMsg);
        }
      }).catch((error) => {
        console.log("Failed to get visitors: " + error);
        NotificationManager.error(error.message, "Error al mostrar Invitaciones", 5000);
      });
    }
  }

  const handleVisit = async (event) => {
    visita = event.value;

    if (event.value === 'EventoGrupal') {
      setIsEvent(true);
      setTitle('Titulo del Evento');
      setTelTitle('Teléfono del Residente');
      setIsEndDateRequired(true);
    }
    else if (event.value === 'Inquilino') {
      setIsEndDateRequired(true);
    }
    else {
      setIsEvent(false);
      setTitle('Nombre del Visitante');
      setTelTitle('Teléfono del Visitante');
      setIsEndDateRequired(false);
    }
  }

  const createNotification = (type, message, title) => {
    switch (type) {
      case 'info':
        NotificationManager.info(message, title, 5000);
        break;
      case 'success':
        NotificationManager.success(message, title, 5000);
        break;
      case 'warning':
        NotificationManager.warning(message, title, 5000);
        break;
      case 'error':
        NotificationManager.error(message, title, 5000);
        break;
      default:
        break;
    }
  };

  let propsIngreso = {
    placeholder: 'E.g. mm/dd/yyyy 12:00 AM',
    required: true
  };

  let propsSalida = {
    placeholder: 'E.g. mm/dd/yyyy 12:00 AM',
    required: true
  };

  return (
    <Container>
      <Content>
        <FormContainer>
          <div className="mx-auto max-w-4xl">
            <h2>Registro de Visitante</h2>
            <form action="#" onSubmit={(event) => handleSubmit(event)}>
              <TwoColumn>
                <Column>
                  <InputContainer>
                    <Label htmlFor="visita">Tipo de Visita</Label>
                    <Dropdown2 id="visita" name="visita" required controlClassName='customDropdown' options={visitTypes} onChange={handleVisit} value={defaultOption} />
                  </InputContainer>
                  <InputContainer>
                    <Label htmlFor="nombre">{title}</Label>
                    <Input id="nombre" type="text" name="nombre" required placeholder="E.g. John Doe" />
                  </InputContainer>
                  {isEvent &&
                    <InputContainer>
                      <Label htmlFor="MaxOpenDoorTimes">Total de Visitantes</Label>
                      <Input id="maxOpenDoorTimes" type="number" name="maxOpenDoorTimes" min="1" max="100" step="1" required placeholder="E.g. 10" />
                    </InputContainer>}
                  <InputContainer tw="flex-1">
                    <Label htmlFor="ingreso">Fecha Ingreso</Label>
                    <Calendar id="ingreso" name="ingreso" required inputProps={propsIngreso} />
                  </InputContainer>
                </Column>
                <Column>
                  <InputContainer>
                    <Label htmlFor="telefono">{telTitle}</Label>
                    <Input
                      id="telefono"
                      type="tel"
                      name="telefono"
                      required
                      placeholder="E.g. +525512345678 ó 5512345678"
                      onChange={(e) => {
                        if (phoneError) validatePhone(e.target.value);
                      }}
                      onBlur={(e) => validatePhone(e.target.value)}
                    />
                    {phoneError && (
                      <span style={{ color: '#fca5a5', fontSize: '0.78rem', marginTop: '4px', display: 'block' }}>
                        {phoneError}
                      </span>
                    )}
                  </InputContainer>
                  {isEndDateRequired &&
                    <InputContainer tw="flex-1">
                      <Label htmlFor="salida">Fecha Salida</Label>
                      <Calendar id="salida" name="salida" required inputProps={propsSalida} />
                    </InputContainer>}
                  {isEvent &&
                    <InputContainer tw="flex-1">
                      <Label htmlFor="lugar">Lugar del Evento</Label>
                      <Input id="lugar" type="text" name="lugar" required placeholder="E.g. Casa #1, Terraza, Asasor, etc." />
                    </InputContainer>
                  }
                  <InputContainer tw="flex-1">
                    <Label htmlFor="comentario">Comentarios (Opcional)</Label>
                    <TextArea id="comentario" name="comentario" placeholder="E.g. Repartidor paqueteria" />
                  </InputContainer>
                </Column>
              </TwoColumn>

              <SubmitButton type="submit" value="Submit">Submit</SubmitButton>
            </form>
          </div>
          <SvgDotPattern1 />
        </FormContainer>
      </Content>

      <NotificationContainer />


      <NotificationContainer />
    </Container>
  );
}


RegisterForm.propTypes = {
  onVisitorCreated: PropTypes.func
};

export default RegisterForm;
